import React, { useRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useServiceProviderDetailApi } from 'src/use-cases/settings/useServiceProvider';
import { openToast } from 'src/use-cases/toast/useToast';
import { ServiceProviderAliasTable } from './tables/ServiceProviderAliasTable';
import { ServiceProviderAddressTable } from './tables/ServiceProviderAddressTable';
import { ServiceProviderAddressDto } from 'src/generated/services/TFinancialApi';

interface ServiceProviderDetailProps {
  serviceProviderId: number | null;
  onClose: () => void;
}

export const ServiceProviderDetail: React.FC<ServiceProviderDetailProps> = ({ serviceProviderId, onClose }) => {
  // Fetch service provider details, aliases, and addresses using the API hook
  const { data: serviceProviderData, isLoading: isLoadingDetail } = useServiceProviderDetailApi(serviceProviderId);

  const serviceProvider = serviceProviderData;
  const aliases = serviceProvider?.data?.nameAliases || [];
  const addresses = serviceProvider?.data?.addresses || [];

  const addressTableRef = useRef<any>(null);
  const aliasTableRef = useRef<any>(null);

  // Handle edit address
  const handleEditAddress = (address: ServiceProviderAddressDto) => {
    openToast({
      toastType: 'info',
      title: 'Edit Address',
      description: `Editing address ID: ${address.id}`,
    });
    // Here you would typically open a modal or navigate to an edit page
    // Implementation for editing address would go here
  };

  if (!serviceProviderId) {
    return null;
  }

  if (isLoadingDetail) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' p={4}>
        <CircularProgress color='primary' />
      </Box>
    );
  }

  if (!serviceProvider) {
    return (
      <div className='p-4'>
        <p className='text-base'>No service provider information found.</p>
      </div>
    );
  }

  return (
    <div className='absolute right-0 top-12 bottom-0 w-full max-w-[720px] bg-white border border-neutral-20 border-r-0 z-50 overflow-y-auto'>
      {/* Alias Section */}
      <div className='relative border-b border-neutral-20'>
        <div className='p-24 flex items-center justify-between gap-2'>
          <div className='flex flex-col gap-4'>
            <h3 className='text-20 font-bold'>Alias</h3>
            <p className='text-14 text-neutral-80'>Service Provider Aliases</p>
          </div>
          <IconButton
            onClick={() => {
              if (aliasTableRef.current) {
                aliasTableRef.current.startCreatingRow();
              }
            }}
            sx={{
              width: '30px',
              height: '30px',
              border: '1px solid #DFDFDF',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: '#00992B',
                '& .MuiSvgIcon-root': {
                  color: '#FFFFFF',
                },
              },
            }}
          >
            <AddIcon sx={{ color: '#00A651', fontSize: 24 }} />
          </IconButton>
        </div>
        <ServiceProviderAliasTable ref={aliasTableRef} aliases={aliases} serviceProviderId={serviceProviderId} />
      </div>
      {/* Move Close Button to top right, outside the Alias flex container */}
      <div className='absolute -top-2 right-4 z-10'>
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </div>

      {/* Address Section */}
      <div className='relative border-b border-neutral-20 mb-[24px]'>
        <div className='p-24 flex items-center justify-between'>
          <div>
            <h3 className='text-20 font-bold mb-2'>Address</h3>
            <p className='text-14 text-neutral-80'>Service Provider Addresses</p>
          </div>
          <IconButton
            onClick={() => {
              // Get reference to the address table component and trigger create mode
              if (addressTableRef.current) {
                addressTableRef.current.setCreatingRow(true);
              }
            }}
            sx={{
              width: '30px',
              height: '30px',
              border: '1px solid #DFDFDF',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: '#00992B',
                '& .MuiSvgIcon-root': {
                  color: '#FFFFFF',
                },
              },
            }}
          >
            <AddIcon sx={{ color: '#00A651', fontSize: 24 }} />
          </IconButton>
        </div>
        <ServiceProviderAddressTable ref={addressTableRef} addresses={addresses} onEditAddress={handleEditAddress} serviceProviderId={serviceProviderId} />
      </div>
    </div>
  );
};

export default ServiceProviderDetail;
